import { useId, useState } from "react";
import { GlobovmsLogo } from "../components/brand/GlobovmsLogo";
import { Icon } from "../components/icons";

function TextField({ id, label, icon, action, children }) {
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-sm font-semibold text-slate-700">{label}</label>
        {action}
      </div>
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-slate-400">
          <Icon name={icon} className="h-[18px] w-[18px]" />
        </span>
        {children}
      </div>
    </div>
  );
}

const inputClass = "h-11 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 text-sm text-brand-ink transition duration-200 focus:border-brand-blue focus:bg-white";

export function Login({ onSubmit, onReset, onClear, error, notice, busy }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState("signin");
  const emailId = useId();
  const passwordId = useId();
  const reset = mode === "reset";

  function switchMode() {
    setMode(reset ? "signin" : "reset");
    setShowPassword(false);
    onClear();
  }

  return (
    <div className="flex min-h-screen bg-[#f4f7fb]">
      <aside className="relative hidden w-[46%] max-w-xl flex-col justify-between overflow-hidden bg-[#0B1C33] px-12 py-10 text-white lg:flex">
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand-blue/20" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-64 w-64 rounded-full bg-brand-orange/15" />
        <div className="relative"><GlobovmsLogo light /></div>
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-blue">Platform</p>
          <h2 className="mt-3 max-w-sm text-4xl font-extrabold leading-tight tracking-tight">Visitor operations, in one place.</h2>
          <p className="mt-4 max-w-sm text-sm leading-6 text-slate-300">Manage client organizations, sites, kiosks, and visitor activity from the Globotech control plane.</p>
          <ul className="mt-8 space-y-3 text-sm text-slate-200">
            <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-brand-orange" />Clients, sites, and staff</li>
            <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-brand-blue" />Visits and kiosk status</li>
            <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-white" />Email delivery and audit</li>
          </ul>
        </div>
        <p className="relative text-xs text-slate-400">Globotech Visitor Management</p>
      </aside>

      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <form
          className="w-full max-w-md overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
          onSubmit={(event) => {
            event.preventDefault();
            if (reset) onReset(email);
            else onSubmit(email, password);
          }}
        >
          <div className="h-1 bg-brand-orange" />
          <div className="p-8">
            <div className="mb-6 lg:hidden"><GlobovmsLogo /></div>
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-sky-50 text-brand-blue">
              <Icon name={reset ? "email" : "lock"} className="h-5 w-5" />
            </div>
            <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-brand-ink">{reset ? "Reset password" : "Platform sign in"}</h1>
            <p className="mt-1.5 text-sm leading-6 text-slate-500">
              {reset ? "We will email a reset link if this address belongs to a platform account." : "Sign in with your Globotech platform account."}
            </p>

            {error ? <p className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800" role="alert">{error}</p> : null}
            {notice ? <p className="mt-5 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2.5 text-sm text-sky-900" role="status">{notice}</p> : null}

            <div className="mt-6 space-y-5">
              <TextField id={emailId} label="Email" icon="email">
                <input id={emailId} className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" />
              </TextField>
              {reset ? null : (
                <TextField
                  id={passwordId}
                  label="Password"
                  icon="lock"
                  action={<button type="button" className="text-xs font-semibold text-brand-blue hover:underline" onClick={switchMode}>Forgot password</button>}
                >
                  <input id={passwordId} className={`${inputClass} pr-11`} type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
                  <button type="button" className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-lg text-slate-400 hover:text-slate-700" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword}>
                    <Icon name={showPassword ? "eyeOff" : "eye"} className="h-[18px] w-[18px]" />
                  </button>
                </TextField>
              )}
              <button className="flex h-11 w-full items-center justify-center rounded-lg bg-brand-orange text-sm font-semibold text-white transition duration-200 hover:bg-[#c4631d] disabled:cursor-not-allowed disabled:opacity-50" disabled={busy}>{busy ? "Please wait" : reset ? "Send reset link" : "Sign in"}</button>
              {reset ? <button type="button" className="w-full text-sm font-semibold text-brand-blue hover:underline" onClick={switchMode}>Back to sign in</button> : null}
            </div>
            <p className="mt-6 border-t border-slate-100 pt-4 text-center text-xs text-slate-400">Globotech platform staff only</p>
          </div>
        </form>
      </main>
    </div>
  );
}

export function LoginStatus({ title, detail, action }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f4f7fb] px-4">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <div className="flex justify-center"><GlobovmsLogo /></div>
        <h1 className="mt-5 text-lg font-extrabold text-brand-ink">{title}</h1>
        {detail ? <p className="mt-2 text-sm leading-6 text-slate-500">{detail}</p> : null}
        {action ? <div className="mt-5">{action}</div> : null}
      </div>
    </div>
  );
}
